import { TimelineChain } from "@/components/timeline/buildTimelineData"
import { DriverDisplayInfo } from "@/reducers/accountChatsSelectors"
import { PlannedOrder } from "@/reducers/plannedOrdersSlice"
import { buildBusyIntervals, SuggestionCandidate } from "./vehicleSuggestions"
import { DAY_IN_MS } from "./plannerGeometry"

// Tylko te cztery pola łańcucha mają tu znaczenie. Wąskie wejście, żeby test
// mógł podać literał, a nie cały TimelineChain z eventami i milestone'ami.
export type RankingChain = Pick<
  TimelineChain,
  "startOffset" | "endOffset" | "startCode" | "endCode"
>

// Kandydaci do rankingów pojazdów: pojazd + jego czas zajęty (bieżąca trasa,
// przypisane planowane zlecenia, łańcuchy z feedów providerów) + planowane
// zlecenia, których rozładunki są pozycjami odniesienia.
//
// Jedno miejsce dla wszystkich rankingów (przypisywanie zlecenia, punkt
// planowania, dojazd drogowy) — inaczej "liczą z tych samych danych" jest
// umową, a nie faktem, i zmiana w budowie interwałów musi być zrobiona
// wielokrotnie albo rankingi się rozjadą.
//
// excludeOrderId: zlecenie właśnie przypisywane nie może być ani własną
// pozycją odniesienia, ani własnym czasem zajętym — to jedyna różnica między
// kandydatami trybu przypisywania i pozostałych.
export const buildRankingCandidates = (
  drivers: DriverDisplayInfo[],
  plannedOrders: PlannedOrder[],
  chainsByChatJid: Map<string, RankingChain[]>,
  timelineStart: Date,
  excludeOrderId?: string,
): SuggestionCandidate[] =>
  drivers
    .filter((driver) => driver.chatJid)
    .map((driver) => {
      const assignedPlannedOrders = plannedOrders.filter(
        (candidateOrder) =>
          candidateOrder.assignmentStatus === "vehicle" &&
          candidateOrder.assignedVehicleChatJid === driver.chatJid &&
          candidateOrder.id !== excludeOrderId,
      )
      const busyIntervals = buildBusyIntervals(driver, assignedPlannedOrders)
      // Provider-feed chains count as busy time too — work synced only via
      // G-Box/Navigator/Platform Science must exclude the vehicle as well.
      for (const chain of chainsByChatJid.get(driver.chatJid!) ?? []) {
        busyIntervals.push({
          start: new Date(
            timelineStart.getTime() + chain.startOffset * DAY_IN_MS,
          ),
          end: new Date(timelineStart.getTime() + chain.endOffset * DAY_IN_MS),
          // "UN" to sentinel nieznanego kraju w feedach — nie flaga.
          startCountryCode:
            chain.startCode === "UN" ? undefined : chain.startCode,
          endCountryCode: chain.endCode === "UN" ? undefined : chain.endCode,
        })
      }
      return { driver, busyIntervals, assignedPlannedOrders }
    })
